import React, { useId } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import type { FlowComponentRenderProps, NpsFormComponentConfig } from '../types';
import HtmlText from '../components/HtmlText';
import InlineFeedback from '../components/rating/InlineFeedback';
import FeedbackRatingSubmitArea from './FeedbackRatingSubmitArea';
import {
  buildRatingValue,
  defaultButtonStyles,
  getRatingState,
  selectedButtonStyles,
} from '../components/rating/ratingUtils';

const FeedbackNpsRating: React.FC<FlowComponentRenderProps<NpsFormComponentConfig>> = ({
  config,
  goNext,
  setValue,
  submitValueAndGoNext,
  value,
  pending,
}) => {
  const questionId = useId();
  const legendId = useId();
  const ratingState = getRatingState(value);
  const selectedValue = ratingState.score;
  const submitted = ratingState.submitted;
  const selectionLocked = submitted || pending === true;
  const lowLabel = config.lowLabel ?? 'Not likely';
  const highLabel = config.highLabel ?? 'Very likely';
  const legacyStyle = ['pills', 'circles', 'tiles', 'segments'].includes(config.layout ?? '')
    ? (config.layout as 'pills' | 'circles' | 'tiles' | 'segments')
    : null;
  const layout = legacyStyle ? 'split' : (config.layout ?? 'split');
  const buttonStyle = config.style ?? legacyStyle ?? 'standard';
  const colorCoding = config.colorCoding === true;
  const detractorMax = Math.min(8, Math.max(0, config.detractorMax ?? 6));
  const neutralMax = Math.min(9, Math.max(detractorMax + 1, config.neutralMax ?? 8));
  const scores = Array.from({ length: 11 }, (_, score) => score);
  const scoreRows =
    layout === 'single_row'
      ? [scores]
      : layout === 'grid_4_4_3'
        ? [scores.slice(0, 4), scores.slice(4, 8), scores.slice(8)]
        : layout === 'grid_3_4_4'
          ? [scores.slice(0, 3), scores.slice(3, 7), scores.slice(7)]
          : layout === 'grouped_bands'
            ? [
                scores.filter((score) => score <= detractorMax),
                scores.filter((score) => score > detractorMax && score <= neutralMax),
                scores.filter((score) => score > neutralMax),
              ]
            : layout === 'vertical'
              ? scores.map((score) => [score])
              : [scores.slice(0, 5), scores.slice(5)];
  const scoreColor = (score: number) =>
    score <= detractorMax
      ? (config.detractorColor ?? '#FECACA')
      : score <= neutralMax
        ? (config.neutralColor ?? '#FEF3C7')
        : (config.promoterColor ?? '#DCFCE7');
  const renderScoreButton = (score: number) => {
    const selected = selectedValue === score;

    return (
      <Button
        key={score}
        aria-pressed={selected}
        disabled={selectionLocked}
        onClick={() => {
          if (selectionLocked) return;
          setValue(config.id, buildRatingValue(config, ratingState, score, false));
          submitValueAndGoNext(config.id, buildRatingValue(config, ratingState, score, true));
        }}
        sx={{
          ...(selected ? selectedButtonStyles : defaultButtonStyles),
          bgcolor: selected
            ? 'var(--cx-brand-button-selected-bg)'
            : colorCoding
              ? scoreColor(score)
              : 'var(--cx-brand-button-bg)',
          color: selected
            ? 'var(--cx-brand-button-selected-text)'
            : colorCoding
              ? '#1C252E'
              : 'var(--cx-brand-button-text)',
          border: 0,
          borderRadius:
            buttonStyle === 'circles'
              ? '50%'
              : buttonStyle === 'pills'
                ? 999
                : buttonStyle === 'segments'
                  ? 0
                  : '6px',
          fontSize: 17,
          fontWeight: 600,
          height: 48,
          minHeight: 48,
          minWidth: 0,
          px: 0,
          boxShadow: selected
            ? '0 8px 8px rgba(145,158,171,0.16)'
            : '0 8px 16px rgba(145,158,171,0.16)',
          transition: selected ? 'none' : undefined,
          '&:hover': {
            bgcolor: selected
              ? 'var(--cx-brand-button-selected-bg)'
              : colorCoding
                ? scoreColor(score)
                : 'var(--cx-brand-button-hover-bg)',
            border: 0,
            color: selected
              ? 'var(--cx-brand-button-selected-text)'
              : colorCoding
                ? '#1C252E'
                : 'var(--cx-brand-button-text)',
          },
          '&.Mui-focusVisible': {
            outline: '2px solid var(--cx-brand-primary)',
            outlineOffset: '2px',
          },
          '&:disabled': {
            color: selected ? 'var(--cx-brand-button-selected-text)' : 'var(--cx-brand-text-muted)',
            opacity: 0.75,
          },
        }}
        type="button"
        variant="text"
      >
        {score}
      </Button>
    );
  };

  return (
    <Card
      sx={{
        bgcolor: 'transparent',
        border: 0,
        borderRadius: 0,
        boxShadow: 'none',
        color: 'var(--cx-brand-text)',
        p: 0,
      }}
    >
      <Stack spacing={0}>
        <HtmlText
          id={questionId}
          component="div"
          html={config.question ?? 'How likely are you to recommend us?'}
          variant="subtitle2"
          sx={{ '&&': { mb: 1.25 }, fontSize: 16, fontWeight: 600, lineHeight: '24px', textAlign: 'center' }}
        />
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Stack
            aria-describedby={legendId}
            aria-labelledby={questionId}
            role="group"
            spacing={buttonStyle === 'segments' ? 0 : 1}
          >
            {scoreRows.map((row, rowIndex) => (
              <Box
                key={`nps-row-${rowIndex}`}
                sx={{
                  alignSelf: layout === 'split' && row.length === 5 ? 'center' : undefined,
                  display: 'grid',
                  gap: buttonStyle === 'segments' ? 0 : buttonStyle === 'tiles' ? 0.4 : 1,
                  gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
                  maxWidth:
                    layout === 'vertical'
                      ? 180
                      : layout === 'split' && row.length === 5
                        ? buttonStyle === 'standard'
                          ? 'calc(100% * 299.5 / 329)'
                          : 'calc((((100% - 40px) / 6) * 5) + 32px)'
                        : undefined,
                  width: '100%',
                }}
              >
                {row.map(renderScoreButton)}
              </Box>
            ))}
          </Stack>
          <Box id={legendId} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: 'var(--cx-brand-text)', fontSize: 14, lineHeight: 1.35 }}
            >
              0 = <Box component="span">{lowLabel}</Box>
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'var(--cx-brand-text)',
                fontSize: 14,
                lineHeight: 1.35,
                textAlign: 'right',
              }}
            >
              10 = <Box component="span">{highLabel}</Box>
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ mb: config.feedbackMode === 'inline' ? 2 : 0 }}>
          <InlineFeedback
            config={config}
            locked={selectionLocked}
            ratingState={ratingState}
            setValue={setValue}
          />
        </Box>
        {submitted && <FeedbackRatingSubmitArea
          config={config}
          continueToNext={goNext}
          onEdit={() =>
            setValue(config.id, {
              ...ratingState,
              submitted: false,
            })
          }
          ratingState={ratingState}
          selectedValue={selectedValue}
          submitted={submitted}
          submitValueAndGoNext={submitValueAndGoNext}
        />}
      </Stack>
    </Card>
  );
};

export default FeedbackNpsRating;
